import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetRegions } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IRegion, Region } from '../../../@types/model/masterData/region/region';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import RegionHttpService from '../../../services/http/masterData/regionHttpService';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { RegionFormValues, IRegionFormValues } from '../../../@types/model/masterData/region/regionFormValues';
import RegionForm from './form/RegionForm';
import { useHistory } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setRegionMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const RegionScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const regions = useAppSelector(x => x.masterData.regions);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<IRegion>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingRegion, setDeletingRegion] = useState<IRegion>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await RegionHttpService.getRegionData();
                    dispatch(dataSetRegions(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading regions.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedRegion(undefined);
        setFormGuid(undefined);
    };

    const editRegion = (region : IRegion) => {
        setSelectedRegion(region);
        setFormGuid(region.guid);
        openDialog();
    };

    const onSubmit = async (values : IRegionFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await RegionHttpService.addOrUpdateRegion(Region(payload));
            const updatedList = upsertArrayElement(regions, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetRegions(updatedList));
            await setRegionMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedRegion ? 'Region updated successfully.' : 'Region added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating region data.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatOrganizations = useCallback((organizationIds : Array<number>) => organizations
        .filter(x => organizationIds.some(y => y === x.id))
        .map(x => x.code).toString().replace(/,/g, ', '), [organizations]);

    const getRows = useCallback(() => regions.filter(x => x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y))), [regions, selectedOrganizationIds]);

    const openDeleteConfirmationPopup = (region : IRegion) => {
        setDeletingRegion(region);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingRegion(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeRegion = async () => {
        if (!deletingRegion) return;
        const updatedRegion = { ...deletingRegion, isActive: false };
        setIsLoading(true);
        try {
            const res = await RegionHttpService.deleteRegion(updatedRegion.id);
            const updatedList = upsertArrayElement(regions, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetRegions(updatedList));
            await setRegionMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Region deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting region.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const getInitialFormValues = useMemo(() => {
        return RegionFormValues(selectedRegion, organizations);
    }, [selectedRegion]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    const rows = getRows();

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <PackmanDialog
                title='Region'
                isEdit={!!selectedRegion}
                isLoading={isLoading}
                isOpen={isDialogOpen}
                onClose={closeDialog}
            >
                <RegionForm
                    isLoading={isLoading}
                    initialValues={getInitialFormValues}
                    onSubmit={onSubmit}
                    onClose={closeDialog}
                />
            </PackmanDialog>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IRegion>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editRegion}
                        enableEditing={hasEditingRight}
                        enableDeleting={r => r.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Organizations', field: 'organizationIds', formatFunction: formatOrganizations, width: 200, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={rows}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Region'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this region?'
                onAccept={removeRegion}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default RegionScreen;
